/**
 * Goal Calculation Engine for Hydration Tracking
 * Implements prorated goal calculation and per-reminder volume calculation
 */

export interface HydrationSettings {
  baseDailyRecommendation?: number; // ml, default 3500
  activeHoursStart: string; // "HH:MM" format
  activeHoursEnd: string; // "HH:MM" format
  reminderInterval: number; // minutes
  activeDays: string[]; // array of day names
}

export interface CalculationResult {
  proratedTarget: number; // ml
  volumePerReminder: number; // ml
  activeHours: number; // decimal hours
  remindersPerDay: number; // count
  explanation: string;
}

/**
 * Parse time string to decimal hours
 * @param timeStr - Time in "HH:MM" format
 * @returns Decimal hours (e.g., "09:30" -> 9.5)
 */
export function parseTimeToDecimal(timeStr: string): number {
  const [hours, minutes] = timeStr.split(':').map(Number);
  return hours + minutes / 60;
}

/**
 * Calculate active hours between start and end time
 * @param startTime - Start time in "HH:MM" format
 * @param endTime - End time in "HH:MM" format
 * @returns Active hours as decimal
 */
export function calculateActiveHours(startTime: string, endTime: string): number {
  const start = parseTimeToDecimal(startTime);
  const end = parseTimeToDecimal(endTime);

  // Handle case where end time is next day (e.g., 22:00 to 06:00)
  if (end <= start) {
    return 24 - start + end;
  }

  return end - start;
}

/**
 * Calculate prorated daily target based on active hours
 * EXACT FORMULA: Prorated Goal = (Base Daily Recommendation / 24 hours) × Active Hours
 * @param baseDailyRecommendation - Base recommendation in ml (default 3500ml for adult male)
 * @param activeHours - Active hours as decimal
 * @returns Prorated target in ml
 */
export function calculateProratedTarget(
  baseDailyRecommendation: number = 3500,
  activeHours: number
): number {
  // EXACT FORMULA as specified in Requirements.md D: (Base Daily Recommendation / 16 hours) × Active Hours
  const proratedTarget = Math.round((baseDailyRecommendation / 16) * activeHours);

  // Ensure minimum of 1000ml and maximum of 5000ml
  return Math.max(1000, Math.min(5000, proratedTarget));
}

/**
 * Calculate volume per reminder
 * EXACT FORMULA: Volume per Reminder = Prorated Goal / (Active Hours × 60 / Reminder Interval)
 * @param proratedGoal - Prorated daily goal in ml
 * @param activeHours - Active hours as decimal
 * @param reminderInterval - Reminder interval in minutes
 * @returns Volume per reminder in ml
 */
export function calculateVolumePerReminder(
  proratedGoal: number,
  activeHours: number,
  reminderInterval: number
): number {
  // EXACT FORMULA as specified: Volume per Reminder = Prorated Goal / (Active Hours × 60 / Reminder Interval)
  const totalReminders = (activeHours * 60) / reminderInterval;

  if (totalReminders === 0) return proratedGoal;

  const volumePerReminder = Math.round(proratedGoal / totalReminders);

  // Ensure reasonable volume per reminder (50ml to 1000ml)
  return Math.max(50, Math.min(1000, volumePerReminder));
}

/**
 * Calculate number of reminders per day
 * @param activeHours - Active hours as decimal
 * @param reminderInterval - Reminder interval in minutes
 * @returns Number of reminders per day
 */
export function calculateRemindersPerDay(activeHours: number, reminderInterval: number): number {
  if (reminderInterval <= 0) return 0;
  const activeMinutes = Math.round(activeHours * 60);
  return Math.floor(activeMinutes / reminderInterval);
}

/**
 * Main calculation function that returns all hydration metrics
 * @param settings - Hydration settings
 * @returns Complete calculation result
 */
export function calculateHydrationGoals(settings: HydrationSettings): CalculationResult {
  const {
    baseDailyRecommendation = 3500,
    activeHoursStart,
    activeHoursEnd,
    reminderInterval,
  } = settings;

  // Calculate active hours
  const activeHours = calculateActiveHours(activeHoursStart, activeHoursEnd);

  // Calculate prorated target
  const proratedTarget = calculateProratedTarget(baseDailyRecommendation, activeHours);

  // Calculate reminders per day
  const remindersPerDay = calculateRemindersPerDay(activeHours, reminderInterval);

  // Calculate volume per reminder
  const volumePerReminder = calculateVolumePerReminder(
    proratedTarget,
    activeHours,
    reminderInterval
  );

  // Generate explanation
  const explanation = generateExplanation({
    baseDailyRecommendation,
    activeHours,
    proratedTarget,
    reminderInterval,
    remindersPerDay,
    volumePerReminder,
    activeHoursStart,
    activeHoursEnd,
  });

  return {
    proratedTarget,
    volumePerReminder,
    activeHours,
    remindersPerDay,
    explanation,
  };
}

/**
 * Generate human-readable explanation of calculations
 */
function generateExplanation(params: {
  baseDailyRecommendation: number;
  activeHours: number;
  proratedTarget: number;
  reminderInterval: number;
  remindersPerDay: number;
  volumePerReminder: number;
  activeHoursStart: string;
  activeHoursEnd: string;
}): string {
  const {
    baseDailyRecommendation,
    activeHours,
    proratedTarget,
    reminderInterval,
    remindersPerDay,
    volumePerReminder,
    activeHoursStart,
    activeHoursEnd,
  } = params;

  return `Based on your active hours (${activeHoursStart} to ${activeHoursEnd} = ${activeHours.toFixed(1)} hours), your daily target is prorated from ${baseDailyRecommendation}ml to ${proratedTarget}ml. With reminders every ${reminderInterval} minutes, you'll receive ${remindersPerDay} reminders per day, each suggesting ${volumePerReminder}ml of water.`;
}

/**
 * Validate hydration settings
 * @param settings - Settings to validate
 * @returns Validation result with errors if any
 */
export function validateHydrationSettings(settings: HydrationSettings): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  // Validate time format
  const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
  if (!timeRegex.test(settings.activeHoursStart)) {
    errors.push('Invalid start time format. Use HH:MM format.');
  }
  if (!timeRegex.test(settings.activeHoursEnd)) {
    errors.push('Invalid end time format. Use HH:MM format.');
  }

  // Validate reminder interval
  if (settings.reminderInterval < 15 || settings.reminderInterval > 240) {
    errors.push('Reminder interval must be between 15 and 240 minutes.');
  }

  // Validate active hours
  if (errors.length === 0) {
    const activeHours = calculateActiveHours(settings.activeHoursStart, settings.activeHoursEnd);
    if (activeHours < 4) {
      errors.push('Active hours must be at least 4 hours.');
    }
    if (activeHours > 20) {
      errors.push('Active hours cannot exceed 20 hours.');
    }
  }

  // Validate active days
  if (!settings.activeDays || settings.activeDays.length === 0) {
    errors.push('At least one active day must be selected.');
  }

  const validDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const invalidDays = settings.activeDays?.filter((day) => !validDays.includes(day)) || [];
  if (invalidDays.length > 0) {
    errors.push(`Invalid day(s): ${invalidDays.join(', ')}`);
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Get recommended reminder intervals with exact specifications
 */
export function getReminderIntervalOptions(): Array<{
  value: number;
  label: string;
  description: string;
  category: string;
  displayName: string;
}> {
  return [
    {
      value: 45,
      label: 'Pro (Recommended)',
      description: '45 minutes - Optimal for most people',
      category: 'recommended',
      displayName: 'Pro (45 mins) - Recommended',
    },
    {
      value: 60,
      label: 'Average',
      description: '60 minutes - Good balance of reminders and convenience',
      category: 'average',
      displayName: 'Average (60 mins)',
    },
    {
      value: 90,
      label: 'Beginner',
      description: '90 minutes - Fewer reminders, easier to start with',
      category: 'beginner',
      displayName: 'Beginner (90 mins)',
    },
  ];
}

/**
 * Calculate daily water intake recommendation based on user profile
 * @param age - User age
 * @param gender - User gender
 * @param activityLevel - Activity level (optional)
 * @returns Recommended daily intake in ml
 */
export function calculateBaseDailyRecommendation(
  age?: number,
  gender?: string,
  activityLevel: 'low' | 'moderate' | 'high' = 'moderate'
): number {
  // Institute of Medicine daily fluid recommendations (water): male 3.7L, female 2.7L
  let baseRecommendation = 3200; // Default when gender unspecified

  if (gender === 'male') {
    baseRecommendation = 3700;
  } else if (gender === 'female') {
    baseRecommendation = 2700;
  }

  // Adjust based on age
  if (age) {
    if (age >= 65) {
      baseRecommendation *= 0.9; // Slightly less for seniors
    } else if (age >= 19) {
      baseRecommendation *= 1.0; // Standard for adults
    } else if (age >= 14) {
      baseRecommendation *= 0.85; // Less for teenagers
    }
  }

  // Adjust based on activity level
  switch (activityLevel) {
    case 'high':
      baseRecommendation *= 1.15;
      break;
    case 'moderate':
      baseRecommendation *= 1.0;
      break;
    case 'low':
      baseRecommendation *= 0.95;
      break;
  }

  // Round to nearest 100ml and ensure reasonable bounds
  const rounded = Math.round(baseRecommendation / 100) * 100;
  return Math.max(2000, Math.min(5000, rounded));
}

/**
 * Calculate complete hydration metrics with exact formulas
 * @param settings - Enhanced hydration settings
 * @returns Complete metrics including all calculations
 */
export function calculateCompleteHydrationMetrics(settings: {
  age?: number;
  gender?: string;
  activeHoursStart: string;
  activeHoursEnd: string;
  reminderInterval: number;
}): {
  baseDailyRecommendation: number;
  activeHours: number;
  proratedGoal: number;
  totalRemindersPerDay: number;
  volumePerReminder: number;
  explanation: string;
} {
  // Calculate base daily recommendation
  const baseDailyRecommendation = calculateBaseDailyRecommendation(
    settings.age,
    settings.gender,
    'moderate'
  );

  // Calculate active hours
  const activeHours = calculateActiveHours(settings.activeHoursStart, settings.activeHoursEnd);

  // Calculate prorated goal using exact formula
  const proratedGoal = calculateProratedTarget(baseDailyRecommendation, activeHours);

  // Calculate total reminders per day
  const totalRemindersPerDay = Math.floor((activeHours * 60) / settings.reminderInterval);

  // Calculate volume per reminder using exact formula
  const volumePerReminder = calculateVolumePerReminder(
    proratedGoal,
    activeHours,
    settings.reminderInterval
  );

  // Generate explanation
  const explanation = `Based on your profile (${settings.gender || 'unspecified'}, age ${settings.age || 'unspecified'}), your base daily recommendation is ${baseDailyRecommendation}ml. With active hours from ${settings.activeHoursStart} to ${settings.activeHoursEnd} (${activeHours.toFixed(1)} hours), your prorated goal is ${proratedGoal}ml. You'll receive ${totalRemindersPerDay} reminders every ${settings.reminderInterval} minutes, each suggesting ${volumePerReminder}ml.`;

  return {
    baseDailyRecommendation,
    activeHours,
    proratedGoal,
    totalRemindersPerDay,
    volumePerReminder,
    explanation,
  };
}
